import React, { useEffect, useMemo, useRef, useState } from "react";
import Index from "../../Index";
import { activeShortCut } from "../../../redux/user/action";

/*
 * Help / Instructions page.
 *
 * All copy lives in this file so it ships with the UI it describes.
 *
 * KEYBOARD SHORTCUTS: the tables below mirror the key handling hard-coded in
 *   component/common/keyboard-shortcuts/useKeyboardShortcut.js  (global keys)
 *   container/user/pages/Dashboard.js -> handleKeyDown          (arrows, F)
 * The shortcut list stored in the backend / Default Settings does NOT drive
 * behaviour, so it is not used here. If you change a key in those files,
 * update SHORTCUT_GROUPS / NUMBER_KEYS / CTRL_KEYS below.
 */

// ---------------------------------------------------------------------------
// Shortcut data
// ---------------------------------------------------------------------------

const SHORTCUT_GROUPS = [
  {
    title: "Getting around",
    rows: [
      { keys: ["S"], action: "Open script search" },
      { keys: ["Space"], action: "Go to the Dashboard" },
      { keys: ["Alt", "Backspace"], action: "Go back to the previous page" },
      { keys: ["H"], action: "Hide header and sidebar (more room for charts)" },
      { keys: ["V"], action: "Show header and sidebar again" },
      { keys: ["Esc"], action: "Clear the highlighted chart" },
    ],
  },
  {
    title: "Picking a chart on the Dashboard",
    rows: [
      { keys: ["↑", "↓", "←", "→"], join: " ", action: "Move the highlight from chart to chart" },
      { keys: ["F"], action: "Add the highlighted chart to Favourites" },
      { keys: ["Shift", "O"], action: "Open the highlighted chart in a large popup" },
    ],
  },
  {
    title: "Chart style",
    rows: [
      { keys: ["L"], action: "Line chart" },
      { keys: ["A"], action: "Area chart" },
      { keys: ["D"], action: "Dotted chart" },
      { keys: ["C"], action: "Candlestick chart" },
      { keys: ["Shift", "S"], action: "Show / hide the time scale (bottom axis)" },
      { keys: ["Shift", "P"], action: "Show / hide the price scale (side axis)" },
    ],
  },
  {
    title: "Chart view",
    rows: [
      { keys: ["I"], action: "Switch to Intraday view (today's market)" },
      { keys: ["P"], action: "Switch to Positional view (several days or more)" },
      { keys: ["1"], to: ["7"], action: "Change candle size or time frame — see the table below" },
    ],
  },
  {
    title: "Trading (on the highlighted chart)",
    note: "These keys stop working after the auto-square time if Auto Square is on in Default Settings.",
    rows: [
      { keys: ["Ctrl", "B"], action: "Tag as Buy" },
      { keys: ["Ctrl", "S"], action: "Tag as Sell" },
      { keys: ["Alt", "W"], action: "Tag as Watch (keep an eye on it, no trade)" },
      { keys: ["Ctrl", "R"], action: "Reverse a Buy ↔ Sell, or remove a Watch tag" },
      { keys: ["Ctrl", "E"], action: "Exit the trade (close the Buy / Sell tag)" },
    ],
  },
  {
    title: "Tracking Window Charts",
    rows: [
      { keys: ["Alt", "T"], action: "Place a Target line, then confirm the price" },
      { keys: ["Alt", "L"], action: "Place a Stop Loss line, then confirm the price" },
      { keys: ["Delete"], action: "Remove the selected line or drawing" },
    ],
  },
];

// Number keys mean different things depending on the current view.
const NUMBER_KEYS = [
  { key: "1", intraday: "1 minute candles", positional: "5 Days" },
  { key: "2", intraday: "2 minute candles", positional: "15 Days" },
  { key: "3", intraday: "3 minute candles", positional: "1 Month" },
  { key: "4", intraday: "4 minute candles", positional: "3 Months" },
  { key: "5", intraday: "5 minute candles", positional: "6 Months" },
  { key: "6", intraday: "10 minute candles", positional: "1 Year" },
  { key: "7", intraday: "15 minute candles", positional: "—" },
];

// Ctrl + number sets the candle size in Positional view, per time frame.
const CTRL_KEYS = [
  { frame: "5 Days", values: ["15 min", "30 min", "1 hour"] },
  { frame: "15 Days", values: ["30 min", "1 hour", "4 hour"] },
  { frame: "1 Month / 3 Months", values: ["4 hour", "1 day"] },
  { frame: "6 Months", values: ["1 day", "2 day", "3 day", "1 week"] },
  { frame: "1 Year", values: ["1 day", "2 day", "3 day", "1 week", "2 week", "3 week"] },
];

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const Kbd = ({ children }) => <kbd className="guide-kbd">{children}</kbd>;

const KeyCombo = ({ keys, join = "+", to }) => (
  <span className="guide-combo">
    {keys.map((k, i) => (
      <React.Fragment key={k}>
        {i > 0 && join.trim() && <span className="guide-plus">{join}</span>}
        {i > 0 && !join.trim() && " "}
        <Kbd>{k}</Kbd>
      </React.Fragment>
    ))}
    {to && (
      <>
        <span className="guide-plus">to</span>
        <Kbd>{to[0]}</Kbd>
      </>
    )}
  </span>
);

const Steps = ({ items }) => (
  <ol className="guide-steps">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ol>
);

const Bullets = ({ items }) => (
  <ul className="guide-bullets">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const Tip = ({ children, tone = "tip" }) => (
  <div className={`guide-callout guide-callout-${tone}`}>
    <strong>{tone === "warn" ? "Good to know: " : "Tip: "}</strong>
    {children}
  </div>
);

const Table = ({ head, rows }) => (
  <div className="guide-table-wrap">
    <table className="guide-table">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Section = ({ id, title, lead, innerRef, children }) => (
  <section id={id} ref={innerRef} className="guide-card">
    <h2 className="guide-card-title">{title}</h2>
    {lead && <p className="guide-card-lead">{lead}</p>}
    {children}
  </section>
);

// ---------------------------------------------------------------------------
// Page sections. `keywords` feeds the search box together with the title.
// ---------------------------------------------------------------------------

const buildSections = () => [
  {
    id: "quick-start",
    title: "Quick start",
    lead: "New here? Follow these six steps and you will have your first trade being tracked in a few minutes.",
    keywords: "start begin new first steps beginner",
    body: (
      <div className="guide-quick-grid">
        {[
          ["Find a script", "Click the magnifier at the top (or press S) and type at least 3 letters of a stock name, e.g. “REL” for Reliance."],
          ["Add it to your watchlist", "Press the + button next to the result. It now appears on your Dashboard."],
          ["Pick a view", "Press I for today's market (Intraday) or P for a longer history (Positional)."],
          ["Tag a trade", "Open the ⋮ menu on the chart and choose Buy or Sell (or press Ctrl+B / Ctrl+S)."],
          ["Set Target and Stop Loss", "Enter the price where you want to take profit (Target) and where you want to cut the loss (Stop Loss)."],
          ["Relax — we watch it for you", "When the price reaches either level you get a notification (bell icon) and the trade appears in your report."],
        ].map(([title, text], i) => (
          <div className="guide-quick-card" key={title}>
            <span className="guide-quick-num">{i + 1}</span>
            <div>
              <h4>{title}</h4>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "how-it-works",
    title: "How the platform works",
    lead: "In one sentence: we bring live market prices to your charts and automatically watch your trades for you.",
    keywords: "live price feed exchange nse nfo mcx candle alert engine how works",
    body: (
      <>
        <div className="guide-flow">
          {["Live market price", "Your charts", "Your Buy / Sell tag", "Automatic alert"].map((s, i, arr) => (
            <React.Fragment key={s}>
              <span className="guide-flow-step">{s}</span>
              {i < arr.length - 1 && <span className="guide-flow-arrow">→</span>}
            </React.Fragment>
          ))}
        </div>
        <Bullets
          items={[
            "Prices come live from the exchange (NSE stocks, NFO futures and MCX commodities) while the market is open.",
            "Each price update is grouped into “candles” — one candle shows how the price moved during one time slot, such as 5 minutes.",
            "Older candles come from our history store, so you can look back over past days, months or a year.",
            "When you tag a trade with a Target and Stop Loss, the system checks every price update and closes the tag for you the moment either level is hit. You do not need to keep the screen open.",
          ]}
        />
      </>
    ),
  },
  {
    id: "screen",
    title: "The screen at a glance",
    lead: "The buttons in the top-right corner of every page.",
    keywords: "header buttons bell notification search magnifier settings gear full screen menu",
    body: (
      <Table
        head={["Button", "What it does"]}
        rows={[
          ["🔔 Bell", "Your alerts. The number shows how many are unread."],
          ["🔍 Magnifier", "Search for a script to view or add to your watchlist."],
          ["📈 Chart / ▦ Grid", "Switch between the Dashboard and the Tracking Window charts."],
          ["⚙ Gear", "Default Settings — display, chart and stop-loss preferences."],
          ["⤢ Expand", "Full-screen mode. Press it again to exit."],
          ["≡ Menu", "Profile, Instructions, Keyboard Shortcuts, Data, Theme and Logout."],
        ]}
      />
    ),
  },
  {
    id: "search",
    title: "Find scripts and build your watchlist",
    lead: "A “script” is any stock, future or commodity you can trade. Your watchlist is the set of scripts shown on your Dashboard.",
    keywords: "search watchlist add remove script symbol name favourite favorite",
    body: (
      <>
        <Steps
          items={[
            "Click the magnifier or press S.",
            "Type at least 3 letters of the symbol or company name.",
            "Click the name to open its chart, or press + to add it to your watchlist.",
            "Press − on a script that is already in your watchlist to remove it.",
          ]}
        />
        <Tip>
          On the Dashboard, highlight a chart with the arrow keys and press F to add it to Favourites.
        </Tip>
      </>
    ),
  },
  {
    id: "charts",
    title: "Reading charts",
    lead: "Choose how much history you see and how each candle is drawn.",
    keywords: "intraday positional interval time frame candle line area dotted layout grid columns download",
    body: (
      <>
        <Table
          head={["View", "Best for", "Candle sizes"]}
          rows={[
            [<strong>Intraday</strong>, "Today's market, short trades", "1, 2, 3, 4, 5, 10 or 15 minutes"],
            [<strong>Positional</strong>, "Trades held for days or weeks", "Pick a time frame (5 Days → 1 Year), then a candle size from 15 minutes to 3 weeks"],
          ]}
        />
        <Bullets
          items={[
            "Chart styles: Line, Area, Dotted and Candlestick. Switch from the sidebar or press L, A, D or C.",
            "See 1, 2, 6 or 9 charts at once using the layout options in the sidebar. Grid Settings and Sorting are there too.",
            "Download a chart as an image or PDF from the download option in the chart header.",
          ]}
        />
        <Tip>Not sure which to use? Start with Intraday and 5-minute candles (press I, then 5).</Tip>
      </>
    ),
  },
  {
    id: "trading",
    title: "Tag a trade: Buy, Sell, Target and Stop Loss",
    lead: "Tagging tells the platform what you are doing with a script so it can track the result for you.",
    keywords: "buy sell watch reverse exit remove tag target stop loss stoploss sl auto square",
    body: (
      <>
        <h4 className="guide-subhead">The tags</h4>
        <Table
          head={["Tag", "Meaning"]}
          rows={[
            [<span className="guide-pill guide-pill-buy">Buy</span>, "You expect the price to go up."],
            [<span className="guide-pill guide-pill-sell">Sell</span>, "You expect the price to go down."],
            [<span className="guide-pill guide-pill-watch">Watch</span>, "Just keep an eye on it — no trade."],
            ["Reverse", "Flip an open Buy into a Sell, or a Sell into a Buy."],
            ["Exit", "Close your trade now, at the current price."],
            ["Remove", "Remove a Watch tag."],
          ]}
        />
        <h4 className="guide-subhead">How to tag</h4>
        <Steps
          items={[
            "Open the ⋮ (three dots) menu on the chart header.",
            "Choose Buy, Sell or Watch. The chart header turns green for Buy, red for Sell and blue for Watch.",
            "Set your Target and Stop Loss. You can change them later from Open Orders, or on Tracking Window Charts with Alt+T / Alt+L.",
          ]}
        />
        <h4 className="guide-subhead">When is a Target or Stop Loss “hit”?</h4>
        <Table
          head={["", "Target is hit when price…", "Stop Loss is hit when price…"]}
          rows={[
            [<span className="guide-pill guide-pill-buy">Buy</span>, "rises to or above the Target", "falls to or below the Stop Loss"],
            [<span className="guide-pill guide-pill-sell">Sell</span>, "falls to or below the Target", "rises to or above the Stop Loss"],
          ]}
        />
        <Tip tone="warn">
          A tag belongs to the view and candle size it was made on. A Buy on Intraday 5-minute is separate from a Buy
          on Positional 1-day for the same script.
        </Tip>
        <Tip tone="warn">
          If Auto Square is on in Default Settings, tagging is locked after the market's auto-square time each day.
        </Tip>
      </>
    ),
  },
  {
    id: "tracking",
    title: "Track your open trades",
    lead: "See all your live trades in one place.",
    keywords: "tracking window charts open orders edit exit live status",
    body: (
      <Bullets
        items={[
          "Tracking Window — a list of your tagged scripts with live price and status.",
          "Tracking Window Charts — the same scripts as live charts with your Target and Stop Loss lines drawn on them.",
          "Open Orders — review every open trade, edit its Target / Stop Loss, or exit it.",
        ]}
      />
    ),
  },
  {
    id: "results",
    title: "Alerts, reports and data",
    lead: "Where to see what happened to your trades.",
    keywords: "notification alert report profit loss data performance hit ratio",
    body: (
      <Table
        head={["Where", "What you see"]}
        rows={[
          ["🔔 Notifications", "Every Target or Stop Loss hit, newest first."],
          ["Report", "Closed trades with entry price, exit price and profit or loss."],
          ["≡ Menu → Data", "Your performance over time, including how often Targets were hit."],
        ]}
      />
    ),
  },
  {
    id: "settings",
    title: "Settings, theme and profile",
    lead: "Make the platform look and behave the way you like.",
    keywords: "theme light medium dark settings default profile photo name mobile",
    body: (
      <Bullets
        items={[
          "Theme — ≡ Menu → Theme → Light, Medium or Dark. Chart colours are remembered for each theme.",
          "Default Settings (⚙) — what to show on charts, which side the toolbar sits, search by symbol or name, Auto Square and more.",
          "Profile — ≡ Menu → Profile to update your name, mobile number, city, state, zip code and photo.",
        ]}
      />
    ),
  },
];

const SHORTCUTS_SECTION = {
  id: "shortcuts",
  title: "Keyboard shortcuts",
  lead: "Do everything faster without the mouse. Shortcuts pause while you are typing in a search box or form.",
  keywords: "keyboard shortcut keys hotkey ctrl alt shift",
};

const GLOSSARY = [
  ["Script", "Any stock, future or commodity you can view or trade."],
  ["Candle", "A bar showing the open, high, low and close price for one time slot."],
  ["Intraday", "Trading within a single day."],
  ["Positional", "Holding a trade for several days or longer."],
  ["Target", "The price where you want to take your profit."],
  ["Stop Loss (SL)", "The price where you want to exit to limit a loss."],
  ["Tag", "A label (Buy, Sell or Watch) you put on a script so we can track it."],
  ["Auto Square", "Automatically closing intraday trades at a fixed time near market close."],
  ["NSE / NFO / MCX", "Stock market / futures & options segment / commodity exchange."],
];

const FAQ = [
  ["The chart is not moving.", "Prices only change while the market is open. Outside market hours you will see the last available price. If the market is open, refresh the page."],
  ["My Buy / Sell options are greyed out.", "Auto Square is on and today's auto-square time has passed. Tagging opens again on the next trading day."],
  ["A keyboard shortcut does nothing.", "Click on an empty area of the page first — shortcuts are paused while a search box or form field is active. Trading keys also need a highlighted chart."],
  ["I didn't get an alert.", "Check the bell icon for unread alerts. An alert is only created when the price actually reaches your Target or Stop Loss."],
  ["Where did my trade go after it hit?", "It closes automatically. Find it under Report and in your Notifications."],
];

const ADMIN_SECTION = {
  id: "admin",
  title: "Admin tools",
  lead: "Only visible to admin accounts.",
  keywords: "admin user management index management",
  body: (
    <>
      <Bullets
        items={[
          "User Management — add, edit and deactivate users (Profile page or sidebar).",
          "Index Management — create indices and manage the scripts inside them.",
        ]}
      />
      <Tip tone="warn">
        Marking an index inactive stops its live price feed. Check with the team before deactivating an index that is in use.
      </Tip>
    </>
  ),
};

// ---------------------------------------------------------------------------

const matches = (query, ...texts) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return texts.some((t) => String(t || "").toLowerCase().includes(q));
};

const Instructions = () => {
  const dispatch = Index.useDispatch();
  const location = Index.useLocation();
  const { displaySettings, twoSideArrowZoom, userLoginDetail } = Index.useSelector(
    (state) => state.UserReducer
  );

  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState("quick-start");
  const [openFaq, setOpenFaq] = useState(false);
  const sectionRefs = useRef({});

  const isAdmin = userLoginDetail?.userType === "admin";
  const trimmed = query.trim();

  const contentSections = useMemo(() => {
    const list = buildSections();
    return isAdmin ? [...list, ADMIN_SECTION] : list;
  }, [isAdmin]);

  const visibleSections = contentSections.filter((s) =>
    matches(trimmed, s.title, s.lead, s.keywords)
  );

  const visibleGroups = SHORTCUT_GROUPS.map((g) => ({
    ...g,
    rows: g.rows.filter((r) =>
      matches(trimmed, g.title, r.action, r.keys.join(" "), SHORTCUTS_SECTION.keywords)
    ),
  })).filter((g) => g.rows.length);
  const showShortcuts =
    visibleGroups.length > 0 || matches(trimmed, SHORTCUTS_SECTION.title, "number ctrl candle time frame");

  const visibleGlossary = GLOSSARY.filter(([term, def]) => matches(trimmed, term, def, "glossary words meaning"));
  const visibleFaq = FAQ.filter(([q, a]) => matches(trimmed, q, a, "faq help problem trouble"));

  const toc = [
    ...visibleSections.map((s) => ({ id: s.id, title: s.title })),
    ...(showShortcuts ? [{ id: "shortcuts", title: "Keyboard shortcuts" }] : []),
    ...(visibleGlossary.length ? [{ id: "glossary", title: "Words you'll see" }] : []),
    ...(visibleFaq.length ? [{ id: "faq", title: "Common questions" }] : []),
  ];
  const nothingFound = toc.length === 0;

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  };

  // Deep link: /user/instructions#shortcuts (used by the header menu).
  useEffect(() => {
    const id = location.hash?.replace("#", "");
    if (id) {
      const t = setTimeout(() => scrollTo(id), 100);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  // Highlight the table-of-contents entry for the section in view.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -60% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [toc.length, trimmed]);

  // Global keyboard shortcuts must not fire while the user types in the search box.
  useEffect(() => () => dispatch(activeShortCut(true)), [dispatch]);

  const refCallbacks = useRef({});
  const register = (id) => {
    if (!refCallbacks.current[id]) {
      refCallbacks.current[id] = (el) => {
        sectionRefs.current[id] = el;
      };
    }
    return refCallbacks.current[id];
  };

  return (
    <>
      <Index.Box className="instructions-main">
        <Index.Header></Index.Header>
        <Index.Overlay></Index.Overlay>
        <Index.Box className="dashboard-wrapper">
          <Index.Box
            className={`main-content-sec instructions-content-sec ${
              displaySettings?.UtilityBar === "left" ? "main-content-sec-right" : ""
            } ${twoSideArrowZoom && "hide-main-content-margin"}`}
          >
            <Index.Box className="guide">
              {/* Hero */}
              <header className="guide-hero">
                <h1 className="guide-title">Help &amp; Instructions</h1>
                <p className="guide-subtitle">
                  Everything you need to start charting, tag your first trade and let the platform track it for you.
                </p>
                <div className="guide-search">
                  <span className="guide-search-icon" aria-hidden="true">
                    <Index.SearchIcon fontSize="small" />
                  </span>
                  <input
                    type="search"
                    className="guide-search-input"
                    placeholder="Search help, e.g. “stop loss” or “Ctrl”"
                    aria-label="Search help"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => dispatch(activeShortCut(false))}
                    onBlur={() => dispatch(activeShortCut(true))}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setQuery("");
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </div>
                <div className="guide-hero-actions">
                  <button type="button" className="guide-chip" onClick={() => scrollTo("quick-start")}>
                    Quick start
                  </button>
                  <button type="button" className="guide-chip" onClick={() => scrollTo("shortcuts")}>
                    Keyboard shortcuts
                  </button>
                  <button type="button" className="guide-chip" onClick={() => scrollTo("faq")}>
                    Common questions
                  </button>
                </div>
              </header>

              <div className="guide-layout">
                {/* Table of contents */}
                {!nothingFound && (
                  <nav className="guide-toc" aria-label="On this page">
                    <p className="guide-toc-label">On this page</p>
                    <ul>
                      {toc.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={`guide-toc-link ${activeId === item.id ? "active" : ""}`}
                            onClick={() => scrollTo(item.id)}
                          >
                            {item.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}

                <div className="guide-content">
                  {nothingFound && (
                    <div className="guide-card guide-empty">
                      <h2 className="guide-card-title">No results for “{trimmed}”</h2>
                      <p className="guide-card-lead">Try a simpler word such as “buy”, “chart” or “alert”.</p>
                      <button type="button" className="guide-chip" onClick={() => setQuery("")}>
                        Clear search
                      </button>
                    </div>
                  )}

                  {visibleSections.map((s) => (
                    <Section key={s.id} id={s.id} innerRef={register(s.id)} title={s.title} lead={s.lead}>
                      {s.body}
                    </Section>
                  ))}

                  {showShortcuts && (
                    <Section id="shortcuts" innerRef={register("shortcuts")} title={SHORTCUTS_SECTION.title} lead={SHORTCUTS_SECTION.lead}>
                      <div className="guide-shortcut-grid">
                        {visibleGroups.map((group) => (
                          <div className="guide-shortcut-group" key={group.title}>
                            <h4 className="guide-subhead">{group.title}</h4>
                            <ul className="guide-shortcut-list">
                              {group.rows.map((row) => (
                                <li key={row.action}>
                                  <span className="guide-shortcut-action">{row.action}</span>
                                  <KeyCombo keys={row.keys} join={row.join} to={row.to} />
                                </li>
                              ))}
                            </ul>
                            {group.note && <p className="guide-note">{group.note}</p>}
                          </div>
                        ))}
                      </div>

                      <h4 className="guide-subhead">Number keys</h4>
                      <p className="guide-note">The same key does a different job depending on the view you are in.</p>
                      <Table
                        head={["Key", "In Intraday view", "In Positional view"]}
                        rows={NUMBER_KEYS.map((r) => [<Kbd>{r.key}</Kbd>, r.intraday, r.positional])}
                      />

                      <h4 className="guide-subhead">Ctrl + number (Positional view)</h4>
                      <p className="guide-note">Changes the candle size. The options depend on the time frame you picked.</p>
                      <Table
                        head={["Time frame", "Ctrl+1", "Ctrl+2", "Ctrl+3", "Ctrl+4", "Ctrl+5", "Ctrl+6"]}
                        rows={CTRL_KEYS.map((r) => [
                          <strong>{r.frame}</strong>,
                          ...Array.from({ length: 6 }, (_, i) => r.values[i] || "—"),
                        ])}
                      />
                    </Section>
                  )}

                  {visibleGlossary.length > 0 && (
                    <Section id="glossary" innerRef={register("glossary")} title="Words you'll see" lead="Plain-English meanings of common trading terms.">
                      <dl className="guide-glossary">
                        {visibleGlossary.map(([term, def]) => (
                          <div key={term} className="guide-glossary-item">
                            <dt>{term}</dt>
                            <dd>{def}</dd>
                          </div>
                        ))}
                      </dl>
                    </Section>
                  )}

                  {visibleFaq.length > 0 && (
                    <Section id="faq" innerRef={register("faq")} title="Common questions">
                      {visibleFaq.map(([q, a]) => (
                        <Index.Accordion
                          key={q}
                          className="guide-faq"
                          expanded={openFaq === q}
                          onChange={(_e, isOpen) => setOpenFaq(isOpen ? q : false)}
                          disableGutters
                        >
                          <Index.AccordionSummary
                            className="guide-faq-summary"
                            expandIcon={<Index.ExpandMore />}
                          >
                            {q}
                          </Index.AccordionSummary>
                          <Index.AccordionDetails className="guide-faq-details">{a}</Index.AccordionDetails>
                        </Index.Accordion>
                      ))}
                    </Section>
                  )}
                </div>
              </div>
            </Index.Box>
          </Index.Box>
        </Index.Box>
      </Index.Box>
    </>
  );
};

export default Instructions;
